/**
 * Mention Parser - Format Functions
 *
 * Functions for formatting mentions in text.
 */

/**
 * Replace @mentions in text with formatted links or spans.
 *
 * Handles both structured tokens (which already embed userId) and legacy mentions.
 * For structured tokens, the formatter receives (displayName, userId).
 * For legacy mentions, the formatter receives (username, resolvedUserId).
 *
 * @param {string} text - Original text
 * @param {Array<{username: string, userId: string}>} resolvedMentions - Resolved mention data
 * @param {function} formatter - Function to format each mention: (displayName, userId) => string
 * @returns {string} Text with @mentions replaced
 */
export function formatMentions(text, resolvedMentions, formatter) {
  if (!text || !resolvedMentions || resolvedMentions.length === 0) {
    return text;
  }

  let result = text;

  for (const { username, userId } of resolvedMentions) {
    // First try to replace structured tokens: @[DisplayName](userId)
    const escapedName = username.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escapedId = userId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const structuredRegex = new RegExp(
      `@\\[${escapedName}\\]\\(${escapedId}\\)`,
      "g",
    );
    const afterStructured = result.replace(structuredRegex, (match) =>
      formatter(username, userId),
    );
    if (afterStructured !== result) result = afterStructured;

    // Then try legacy: @username (case-insensitive)
    const legacyRegex = new RegExp(`@${escapedName}\\b`, "gi");
    const afterLegacy = result.replace(legacyRegex, (match) =>
      formatter(username, userId),
    );
    if (afterLegacy !== result) result = afterLegacy;
  }

  return result;
}
